import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useAuth } from '../AuthContext';
import {
  fetchTooltipCopy,
  resetTooltipCopy,
  saveTooltipCopy,
  TooltipOverride,
} from '../api/tooltipCopyApi';

/**
 * Universal tooltip copy overrides — one fetch per session, resolved inside
 * TendTooltip so callsites stay registry-addressed (the FeedbackCounts
 * pattern). canEdit mirrors the server's universal-admin gate (SuperAdmin /
 * Customer Success) so the edit affordance and the API agree by
 * construction. Consumers outside the provider get the inert default:
 * registry copy, no editing.
 */

interface TooltipCopyState {
  overrides: Record<string, TooltipOverride>;
  canEdit: boolean;
  save: (annotationKey: string, override: TooltipOverride) => Promise<void>;
  reset: (annotationKey: string) => Promise<void>;
}

const INERT: TooltipCopyState = {
  overrides: {},
  canEdit: false,
  save: async () => undefined,
  reset: async () => undefined,
};

const TooltipCopyContext = createContext<TooltipCopyState>(INERT);

export const useTooltipCopy = (): TooltipCopyState => useContext(TooltipCopyContext);

export const TooltipCopyProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [overrides, setOverrides] = useState<Record<string, TooltipOverride>>({});
  const [canEdit, setCanEdit] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) {
      setOverrides({});
      setCanEdit(false);
      return;
    }
    let alive = true;
    fetchTooltipCopy()
      .then((data) => {
        if (!alive) return;
        setOverrides(data.overrides || {});
        setCanEdit(!!data.can_edit);
      })
      .catch(() => { /* non-fatal: registry defaults render */ });
    return () => { alive = false; };
  }, [isAuthenticated]);

  const save = useCallback(async (annotationKey: string, override: TooltipOverride) => {
    setOverrides(await saveTooltipCopy(annotationKey, override));
  }, []);

  const reset = useCallback(async (annotationKey: string) => {
    setOverrides(await resetTooltipCopy(annotationKey));
  }, []);

  return (
    <TooltipCopyContext.Provider value={{ overrides, canEdit, save, reset }}>
      {children}
    </TooltipCopyContext.Provider>
  );
};

export default TooltipCopyContext;
